'use client'

import { useEffect, useRef, useState } from 'react'
import Image from 'next/image'
import { Copy, Check } from 'lucide-react'
import { TrackedCtaLink } from '@/components/analytics/tracked-cta-link'

const useCases = [
  {
    id: 'performance-marketer',
    label: 'Performance Marketer',
    activeBgClass:
      'bg-gradient-to-br from-[#4f9cfb] via-[#2b2b2b] to-[#1f1f1c]',
    inactiveBgClass: 'bg-white/10',
    bgImageSrc: 'https://cdn.soku.ai/marketing/images/soku-ai/usecase-bg-1.png',
    bgImageAlt: 'Performance marketer background',
    questions: [
      'Why did CPA spike yesterday across Meta and Google?',
      'Which campaigns spent the most with zero conversions this week?',
      'Where should we reallocate budget for the best ROAS?',
      'Compare Meta vs Google vs TikTok efficiency over 30 days.',
      'Which audiences are outperforming across all ad platforms?',
      'Show me fatigued creatives that need refreshing.',
      'What budget moves protect ROI this week?',
      'Which placements are driving the cheapest conversions?',
    ],
  },
  {
    id: 'seo-specialist',
    label: 'SEO Specialist',
    activeBgClass:
      'bg-gradient-to-br from-[#4fd6c4] via-[#2b2b2b] to-[#1f1f1c]',
    inactiveBgClass:
      'bg-gradient-to-br from-[#4fd6c4]/20 via-[#203235] to-[#1f1f1c]',
    bgImageSrc: 'https://cdn.soku.ai/marketing/images/soku-ai/usecase-bg-2.png',
    bgImageAlt: 'SEO specialist background',
    questions: [
      'What keyword gaps do we have vs our top 3 competitors?',
      'Which pages lost rankings in the last 14 days and why?',
      'Show me pages ranking positions 4\u201310 that could reach top 3.',
      'What internal linking opportunities are we missing?',
      'Which existing posts should we update to recapture traffic?',
      'Suggest 10 new content topics based on trending search queries.',
      'Compare our domain authority trend vs competitors over 6 months.',
      'Draft a content brief for our highest-opportunity keyword gap.',
    ],
  },
  {
    id: 'social-media-manager',
    label: 'Social Media Manager',
    activeBgClass:
      'bg-gradient-to-br from-[#f2b46b] via-[#2b2b2b] to-[#1f1f1c]',
    inactiveBgClass:
      'bg-gradient-to-br from-[#f2b46b]/20 via-[#2b2520] to-[#1f1f1c]',
    bgImageSrc: 'https://cdn.soku.ai/marketing/images/soku-ai/usecase-bg-3.png',
    bgImageAlt: 'Social media manager background',
    questions: [
      'Which posts had the highest engagement rate this week?',
      'What\u2019s our follower growth trend across all platforms?',
      'Which content format (reels, carousels, stories) converts best?',
      'What are the best posting times based on engagement data?',
      'Compare TikTok vs Instagram Reels performance this month.',
      'Draft 5 post ideas based on our top-performing themes.',
      'Which hashtag strategies are working best for reach?',
      'Which influencer collaborations drove the most profile visits?',
    ],
  },
  {
    id: 'email-crm',
    label: 'Email / CRM Marketer',
    activeBgClass:
      'bg-gradient-to-br from-[#f59e0b] via-[#2b2b2b] to-[#1f1f1c]',
    inactiveBgClass:
      'bg-gradient-to-br from-[#f59e0b]/20 via-[#2b2520] to-[#1f1f1c]',
    bgImageSrc: 'https://cdn.soku.ai/marketing/images/soku-ai/usecase-bg-1.png',
    bgImageAlt: 'Email CRM marketer background',
    imageFlip: 'scale-y-[-1]' as const,
    questions: [
      'Which email campaigns had the highest open and click-through rates?',
      'What subject lines drove the best performance this month?',
      'Which audience segments have the lowest unsubscribe rates?',
      'Compare drip sequence conversions across customer lifecycle stages.',
      'What send times generate the highest engagement?',
      'Which automated flows are underperforming vs benchmarks?',
      'Show me re-engagement campaign results for churned subscribers.',
      'Draft A/B test ideas for our next product launch email.',
    ],
  },
  {
    id: 'cmo-growth',
    label: 'CMO / Growth Lead',
    activeBgClass:
      'bg-gradient-to-br from-[#fb923c] via-[#2b2b2b] to-[#1f1f1c]',
    inactiveBgClass:
      'bg-gradient-to-br from-[#fb923c]/20 via-[#2b2520] to-[#1f1f1c]',
    bgImageSrc: 'https://cdn.soku.ai/marketing/images/soku-ai/usecase-bg-2.png',
    bgImageAlt: 'CMO growth lead background',
    imageFlip: 'scale-x-[-1]' as const,
    questions: [
      'What is blended CAC across all channels this week vs last?',
      'Which marketing channel is scaling most profitably right now?',
      'Show the full-funnel conversion rate from awareness to purchase.',
      'How does organic vs paid contribute to revenue this quarter?',
      'What\u2019s the email + retargeting contribution to overall revenue?',
      'Compare customer acquisition cost by channel over 90 days.',
      'What is the projected revenue impact if we double SEO investment?',
      'Which customer segments have the highest lifetime value?',
    ],
  },
]

export function UseCasesSection() {
  const [activeUseCaseId, setActiveUseCaseId] = useState(useCases[0].id)
  const [copiedQuestion, setCopiedQuestion] = useState<string | null>(null)
  const copyResetRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const handleCopy = async (text: string) => {
    if (typeof navigator !== 'undefined' && navigator.clipboard) {
      await navigator.clipboard.writeText(text)
      setCopiedQuestion(text)
      if (copyResetRef.current) {
        clearTimeout(copyResetRef.current)
      }
      copyResetRef.current = setTimeout(() => {
        setCopiedQuestion(null)
      }, 1500)
    }
  }

  useEffect(() => {
    const timeout = setTimeout(() => {
      setActiveUseCaseId(currentId => {
        const currentIndex = useCases.findIndex(item => item.id === currentId)
        const nextIndex =
          currentIndex === -1 ? 0 : (currentIndex + 1) % useCases.length
        return useCases[nextIndex]?.id ?? useCases[0].id
      })
    }, 5000)

    return () => clearTimeout(timeout)
  }, [activeUseCaseId])

  return (
    <section className="bg-ink py-12 sm:py-16 md:py-24">
      <div className="mx-auto flex w-full max-w-6xl flex-col gap-8 px-5 sm:gap-12 sm:px-6">
        <div className="flex flex-col items-center text-center">
          <span className="inline-flex items-center gap-2 rounded-md border border-white/40 px-3 py-1 text-xs font-medium text-white/80">
            Use Cases
          </span>
          <h2 className="mt-4 text-2xl font-medium tracking-tight text-white sm:text-3xl md:text-4xl">
            How Soku AI Helps Your Team
          </h2>
        </div>

        <div className="grid gap-6 lg:grid-cols-[360px_minmax(0,1fr)] lg:gap-8">
          <div className="-mx-5 flex gap-3 overflow-x-auto px-5 pb-1 sm:mx-0 sm:px-0 lg:flex-col">
            {useCases.map(item => {
              const isActive = item.id === activeUseCaseId
              return (
                <button
                  key={item.id}
                  type="button"
                  aria-pressed={isActive}
                  onClick={() => setActiveUseCaseId(item.id)}
                  className={`relative flex h-[72px] w-[200px] shrink-0 cursor-pointer items-end overflow-hidden rounded-xl border px-4 py-3 text-left text-sm tracking-tight text-white transition hover:border-white/50 sm:w-[220px] sm:text-base lg:w-auto ${
                    isActive ? 'border-white/80' : 'border-white/20'
                  }`}
                >
                  <Image
                    src={item.bgImageSrc}
                    alt={item.bgImageAlt}
                    fill
                    sizes="(max-width: 1024px) 220px, 360px"
                    className={`object-cover opacity-90 ${'imageFlip' in item ? item.imageFlip : ''}`}
                  />
                  <div
                    className={`absolute inset-0 ${
                      isActive ? item.activeBgClass : item.inactiveBgClass
                    } opacity-80`}
                  />
                  <span className="relative z-10">{item.label}</span>
                </button>
              )
            })}
          </div>

          <div className="bg-muted rounded-2xl p-3 pt-0 sm:p-4">
            <div className="grid [&>*]:col-start-1 [&>*]:row-start-1">
              {useCases.map(uc => (
                <div
                  key={uc.id}
                  className={`mt-3 space-y-2.5 transition-opacity duration-200 sm:mt-4 sm:space-y-3 ${uc.id === activeUseCaseId ? 'visible opacity-100' : 'invisible opacity-0'}`}
                  aria-hidden={uc.id !== activeUseCaseId}
                >
                  {uc.questions.map((question, index) => (
                    <div
                      key={`${question}-${index}`}
                      className="flex items-center justify-between gap-3 rounded-xl bg-white px-3 py-2.5 sm:px-4 sm:py-3"
                    >
                      <span className="text-foreground text-sm sm:text-base">
                        {question}
                      </span>
                      <div className="flex shrink-0 items-center gap-2">
                        <button
                          type="button"
                          aria-label={
                            copiedQuestion === question
                              ? 'Copied question'
                              : 'Copy question'
                          }
                          onClick={() => handleCopy(question)}
                          className={`flex h-8 w-8 cursor-pointer items-center justify-center rounded-lg border transition ${
                            copiedQuestion === question
                              ? 'border-green-600/40 bg-green-600/10'
                              : 'border-foreground/20'
                          }`}
                        >
                          {copiedQuestion === question ? (
                            <Check className="h-4 w-4" />
                          ) : (
                            <Copy className="h-4 w-4" />
                          )}
                        </button>
                        <TrackedCtaLink
                          href="/sign-up"
                          event="marketing_cta_click"
                          eventProperties={{
                            cta_text: 'Try this question',
                            cta_location: 'use_cases',
                            question,
                            use_case: uc.id,
                          }}
                          className="border-foreground/20 hidden items-center rounded-full border px-4 py-1 text-sm sm:inline-flex"
                        >
                          Try this question
                        </TrackedCtaLink>
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
